// 登录 / 注册门：未登录时展示，登录成功后进入主应用
import { useState, type FormEvent } from 'react'
import { useApp } from '../app-context'
import { useI18n } from '../i18n'

type Mode = 'login' | 'register'

export default function AuthView({ onClose }: { onClose?: () => void }) {
  const { login, register } = useApp()
  const { t, locale, setLocale } = useI18n()
  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [nickname, setNickname] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const friendlyError = (msg: string): string => {
    const m = msg.toLowerCase()
    if (m.includes('invalid email')) return t('auth.error.invalidEmail')
    if (m.includes('password too weak')) return t('auth.error.passwordWeak')
    if (m.includes('already registered')) return t('auth.error.emailExists')
    if (m.includes('wrong email or password')) return t('auth.error.wrong')
    if (m.includes('rate limited')) return t('auth.error.rateLimited')
    if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('http 5')) return t('auth.error.network')
    return t('auth.error.generic')
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy) return
    setError('')
    const em = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em)) {
      setError(t('auth.error.invalidEmail'))
      return
    }
    if (password.length < 8) {
      setError(t('auth.error.passwordWeak'))
      return
    }
    setBusy(true)
    try {
      if (mode === 'login') await login(em, password)
      else await register(em, password, nickname.trim())
      onClose?.()
    } catch (err) {
      setError(friendlyError(String(err)))
      setBusy(false)
    }
  }

  return (
    <div className="auth-overlay">
      <div className="auth-screen">
        <button className="auth-close" onClick={onClose} aria-label={t('auth.skip')}>✕</button>
      <div className="auth-card">
        <div className="auth-brand">
          <span className="brand-mark auth-mark">☰</span>
          <h1>{t('app.name')}</h1>
          <p>{t('app.tagline')}</p>
        </div>

        <div className="auth-tabs">
          <button className={`auth-tab${mode === 'login' ? ' active' : ''}`} onClick={() => { setMode('login'); setError('') }}>
            {t('auth.login')}
          </button>
          <button className={`auth-tab${mode === 'register' ? ' active' : ''}`} onClick={() => { setMode('register'); setError('') }}>
            {t('auth.register')}
          </button>
        </div>

        <form className="auth-form" onSubmit={submit}>
          {mode === 'register' && (
            <div className="field">
              <label>{t('auth.nickname')}</label>
              <input
                className="input"
                value={nickname}
                maxLength={20}
                autoComplete="nickname"
                placeholder={t('auth.nicknamePlaceholder')}
                onChange={(e) => setNickname(e.target.value)}
              />
            </div>
          )}
          <div className="field">
            <label>{t('auth.email')}</label>
            <input
              className="input"
              type="email"
              value={email}
              autoComplete="email"
              placeholder="you@example.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label>{t('auth.password')}</label>
            <input
              className="input"
              type="password"
              value={password}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              placeholder="••••••••"
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && <div className="auth-error">{error}</div>}

          <button className="btn btn-primary auth-submit" disabled={busy}>
            {busy ? t('common.loading') : mode === 'login' ? t('auth.loginBtn') : t('auth.registerBtn')}
          </button>
        </form>

        <p className="auth-note">{t('auth.dataNote')}</p>

        {onClose && (
          <button className="btn btn-ghost auth-skip" onClick={onClose}>
            {t('auth.skip')}
          </button>
        )}

        <div className="auth-lang">
          <button className="chip-btn" onClick={() => setLocale(locale === 'zh' ? 'en' : 'zh')}>
            {locale === 'zh' ? 'English' : '中文'}
          </button>
        </div>
      </div>
      </div>
    </div>
  )
}
